import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { useBreadcrumb } from "../../../contexts/BreadcrumbContext.jsx";
import { backendGet } from "../../../services/dataService.js";
import Spinner from "../../../components/Spinner.jsx";
import CustomerDetailsCard from "./components/CustomerDetailsCard.jsx";
import InternalClaimReportSettingsPanel from "./components/InternalClaimReportSettingsPanel.jsx";
import { PageHeader } from "../../../theme/components/index.js";

export default function CustomerShowPage() {
  const { customerId } = useParams();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();

  const [customer, setCustomer] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      await backendGet(`data/kundes/${customerId}`, {
        label: "Kundendaten",
        enqueueSnackbar,
        setIsLoading,
        setResult: (res) => {
          setCustomer(res.customer || res.data);
          // recipients can be filled from res if/when backend supports it
        },
      });
    } catch (error) {
      enqueueSnackbar("Kundendaten konnten nicht geladen werden.", {
        variant: "error",
      });
      // eslint-disable-next-line no-console
      console.error("Failed to fetch customer data", error);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const customerDisplayName = customer
    ? customer.name1 || `Kunde #${customerId}`
    : `Kunde #${customerId}`;

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Kunden", path: "/admin/kunden" },
      {
        name: customerDisplayName,
        path: `/admin/kunden/${customerId}`,
      },
    ]);
  }, [setBreadcrumbs, customerDisplayName, customerId]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!customer) {
    return (
      <Box>
        <PageHeader title="Kundendetails" />
        <Typography variant="h3" sx={{ mb: 3 }}>
          Kunde nicht gefunden
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Kundendetails" />

      <CustomerDetailsCard customer={customer} />

      <InternalClaimReportSettingsPanel
        customerId={customerId}
        recipients={recipients}
        onUpdate={fetchData}
      />
    </Box>
  );
}
